import { inject, Injectable } from "@angular/core";
import { PatientsService } from "../../../api/services";
import { environment } from "../../../environments/environment";
import {
  CreatePatientDto,
  FilterPatientDto,
  ResponsePatientDto,
  UpdatePatientDto,
} from "../../../api/models";
import { Observable } from "rxjs";

const TENANT = environment.tenant;

@Injectable({
  providedIn: "root",
})
export class PatientsApiService {
  /** The patients service */
  private readonly patientsService: PatientsService = inject(PatientsService);

  /**
   * Creates a new patient.
   * @param patient The patient to create.
   * @returns The created patient.
   */
  public create$(body: CreatePatientDto): Observable<ResponsePatientDto> {
    return this.patientsService.patientsControllerCreate({ body });
  }

  /**
   * Updates a patient.
   * @param patient The patient to update.
   * @returns The updated patient.
   */
  public update$(
    id: string,
    body: UpdatePatientDto,
  ): Observable<ResponsePatientDto> {
    return this.patientsService.patientsControllerUpdate({
      tenant: TENANT,
      id,
      body,
    });
  }

  /**
   * Deletes a patient.
   * @param patient The patient to delete.
   * @returns The deleted patient.
   */
  public delete$(id: string): Observable<void> {
    return this.patientsService.patientsControllerDelete({
      tenant: TENANT,
      id,
    });
  }

  /**
   * Retrieves a patient.
   * @param patient The patient to retrieve.
   * @returns The retrieved patient.
   */
  public findOne$(id: string): Observable<ResponsePatientDto> {
    return this.patientsService.patientsControllerFindOne({
      tenant: TENANT,
      id,
    });
  }

  /**
   * Filters patients.
   * @param filterDto The filter DTO.
   * @returns The filtered patients.
   */
  public filter$(
    filterDto: Partial<FilterPatientDto>,
  ): Observable<ResponsePatientDto[]> {
    const body: FilterPatientDto = {
      ...filterDto,
      tenant: TENANT,
    };

    return this.patientsService.patientsControllerFilter({
      body,
    });
  }
}
